import { Clinic } from '@app/core/models';
import { ShareRecommendationMethod } from '@app/shared/utils';
import { ClinicUtilities } from '@app/shared/utils/enums/feature.enum';
import { PriceType } from '@app/shared/utils/enums/price-type.enum';
import { createSelector } from '@ngrx/store';
import { AppState } from '..';
import { mapCompanyAddress } from '@app/shared/utils/static-helpers/vet-helper';
import { FeatureToggle } from '@app/core/models/feature-toggles';

const vetState = (state: AppState) => state.vet;

/**
 * Vet selectors
 */
export const selectVet = createSelector(vetState, (state) => state.vet);
export const selectVetId = createSelector(selectVet, (vet) => vet?.id);
export const selectIsVetValid = createSelector(vetState, (state) => state.isVetValid);
export const selectPreferredLanguage = createSelector(selectVet, (vet) => (vet?.user ? vet.user.preferredLanguage : null));
export const selectSystemPreference = createSelector(selectVet, (vet) => (vet ? vet.systemPreference : null));
export const selectCompanyAddress = createSelector(selectVet, (vet) => mapCompanyAddress(vet?.company?.addresses[0]));

/**
 * Select okta user
 * Raw data from Okta
 */
export const selectOktaUser = createSelector(vetState, (state) => state.oktaUser);

/**
 * Select invitation
 */
export const selectInvitation = createSelector(vetState, (state) => state.invitation);
/**
 * Select market has reco sharing consent flag
 */
export const selectHasRecoSharingConsent = createSelector(vetState, (state) => state.hasRecoSharingConsent);

/**
 * Clinic selectors
 */
export const selectCurrentClinicId = createSelector(vetState, (state) => state.currentClinicId);

export const selectClinics = createSelector(selectVet, (vet) => (vet?.clinics ? vet.clinics : []));

export const selectClinic = createSelector(selectClinics, selectCurrentClinicId, (clinics, currentClinicId) =>
  clinics?.find((c) => c.id === currentClinicId),
);

export const selectCurrentClinicCountry = createSelector(selectClinic, (clinic) => clinic?.companyAddress?.country);

export const selectIsClinicAddressComplete = createSelector(
  selectClinic,
  ({ companyAddress }: Clinic = <Clinic>{}) => !!(companyAddress?.address1 && companyAddress?.zip && companyAddress?.city),
);

export const selectIsIndividualisEnabled = createSelector(selectClinic, (clinic) => {
  return clinic?.companyRoles?.indexOf(ClinicUtilities.individualis) > -1;
});

export const selectIsGrowthChartEnabled = createSelector(selectClinic, (clinic) => {
  return clinic?.companyRoles?.indexOf(ClinicUtilities.growthChart) > -1;
});

export const selectIsPostSterilizationEnabled = createSelector(selectClinic, (clinic) => {
  return clinic?.companyRoles?.indexOf(ClinicUtilities.postSterilizationLetter) > -1;
});

export const selectIsSrCriteriaGenericEnabled = createSelector(selectClinic, (clinic) => {
  return clinic?.companyRoles?.indexOf(ClinicUtilities.srCriteriaGeneric) > -1;
});

export const selectIsClinicalAllianceEnabled = createSelector(selectClinic, (clinic) => {
  return clinic?.companyRoles?.indexOf(ClinicUtilities.clinicalAlliance) > -1;
});

export const selectIsTreatsEnabled = createSelector(selectClinic, (clinic) => {
  return clinic?.companyRoles?.indexOf(ClinicUtilities.treats) > -1;
});

export const selectIsPriceUploadEnabled = createSelector(selectClinic, (clinic) => {
  return clinic?.companyRoles?.indexOf(ClinicUtilities.priceUpload) > -1;
});

export const selectIsEBoutiqueLinksEnabled = createSelector(selectClinic, (clinic) => {
  return clinic?.companyRoles?.indexOf(ClinicUtilities.eBoutiqueLinks) > -1;
});

export const selectIsVetSourceEnabled = (clinicId: string) =>
  createSelector(selectClinics, (clinics) =>
    clinics.filter(({ id }) => id === clinicId).every(({ companyRoles }) => companyRoles.includes(ClinicUtilities.vetSources)),
  );

/**
 * Feature toggle selectors
 */
export const selectFeatureToggles = createSelector(vetState, (state) => state.featureToggles);

export const selectIsFeatureEnabled = (feature: FeatureToggle) =>
  createSelector(selectFeatureToggles, (featureToggles) => featureToggles?.indexOf(feature) > -1);

/**
 * Market selectors
 */
export const selectMarket = createSelector(vetState, selectCurrentClinicCountry, (state, country) => {
  if (!country || state.market?.countryCode === country) {
    return state.market;
  }
});

export const selectMarketQuicklinks = createSelector(selectMarket, (market) => {
  return market?.quicklinks || [];
});

/**
 * Select share recommendation by email is authorized for this market
 */
export const selectCanShareRecoByEmail = createSelector(selectMarket, (market) => {
  return !market?.shareRecommendationMethods ? true : market.shareRecommendationMethods.includes(ShareRecommendationMethod.EMAIL);
});

/**
 * Select share recommendation by qrcode is authorized for this market
 */
export const selectCanShareRecoByQrcode = createSelector(selectMarket, (market) => {
  return !market?.shareRecommendationMethods ? true : market.shareRecommendationMethods.includes(ShareRecommendationMethod.QRCODE);
});

/**
 * Select share recommendation by print is authorized for this market
 */
export const selectCanShareRecoByPrint = createSelector(selectMarket, (market) => {
  return !market?.shareRecommendationMethods ? true : market.shareRecommendationMethods.includes(ShareRecommendationMethod.PRINT);
});

/**
 * Check wheter retail prices are activated, by default true if nothing is set
 */
export const selectIsRetailPriceActivated = createSelector(selectMarket, (market) =>
  !market?.activatedPrices ? true : market.activatedPrices.includes(PriceType.RETAIL),
);
/**
 * Check wheter wholesage prices are activated, by default false if nothing is set
 */
export const selectIsWholesalePriceActivated = createSelector(selectMarket, (market) =>
  !market?.activatedPrices ? false : market.activatedPrices.includes(PriceType.WHOLESALE),
);

export const selectWholesalePrices = createSelector(selectMarket, (market) => market?.wholesalePrices);
export const selectIsSptRetailEnable = createSelector(selectMarket, (market) => market?.enableSptRetail);
export const selectIsSolProductsInVetEnable = createSelector(selectMarket, (market) => market?.enableSolProductsInVet);
export const selectIsKpiFileEnable = createSelector(selectMarket, (market) => market?.enableKpiFile);

export const selectIsInvitationOnboardingEnable = createSelector(selectMarket, (market) => !(market?.disableOnboardingInvitation ?? false));

export const selectMarketMailLanguage = createSelector(selectMarket, (market) => market?.mailLanguages);
export const selectHideDietBlockInPoLetter = createSelector(selectMarket, (market) => market?.hideDietBlockInPoLetter ?? false);

/**
 * Clinic & Market selectors
 */
/**
 * Select current clinic currency code based on country
 */
export const selectCurrentClinicCurrency = createSelector(selectClinic, selectMarket, (clinic, market) => {
  if (market?.countryCode === clinic?.companyAddress?.country) {
    return market?.currency;
  }
});

export const selectConversionConfiguration = createSelector(
  selectClinic,
  selectMarket,
  // uncomment to test IVO during development mode
  // (clinic, market) => market?.conversionConfigurations?.find((config) => config.type === 'VS_CV') || null
  (clinic, market) => market?.conversionConfigurations?.find((config) => clinic?.companyRoles?.includes(config.type)) || null,
);

/**
 * Preferences selectors
 */
const selectPreferences = createSelector(vetState, (state) => state.preferences);
export const selectPreferredMaxPetWeight = createSelector(selectPreferences, (preferences) => preferences?.maxPetWeight);
export const selectCurrentBigMeasurementUnit = createSelector(selectPreferences, (preferences) => preferences?.currentBigMeasurementUnit);
export const selectCurrentSmallMeasurementUnit = createSelector(
  selectPreferences,
  (preferences) => preferences?.currentSmallMeasurementUnit,
);
export const selectCurrentSystemPreferenceCode = createSelector(
  selectPreferences,
  (preferences) => preferences?.currentSystemPreferenceCode,
);
